import React, { useEffect } from 'react'
import styled from 'styled-components'
import { useNavigate } from 'react-router-dom'

const Container = styled.div`
  width: 100%;
  min-height: 100vh;
  background-color: #ffffff;
  font-family: -apple-system, system-ui, BlinkMacSystemFont, 'Segoe UI', Helvetica, Arial, sans-serif;
  font-size: 18px;
  line-height: 1.8;
  color: #2d3748;
`

const HeroSection = styled.section`
  padding-top: 100px;
  padding-bottom: 60px;
  background: linear-gradient(135deg, #6366f1 0%, #4f46e5 100%);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, rgba(99, 102, 241, 0.9) 0%, rgba(79, 70, 229, 0.9) 100%);
    z-index: 0;
  }

  @media (max-width: 800px) {
    padding-top: 80px;
    padding-bottom: 40px;
  }
`

const MaxWidthContainer = styled.div`
  max-width: 1300px;
  margin: 0 auto;
  padding: 0 30px;
  position: relative;
  z-index: 1;

  @media (max-width: 800px) {
    padding: 0 20px;
  }
`

const HeroContent = styled.div`
  text-align: center;
  max-width: 900px;
  margin: 0 auto;
`

const ProductLogo = styled.h1`
  font-size: 56px;
  font-weight: 700;
  color: #ffffff;
  margin-bottom: 20px;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

  @media (max-width: 800px) {
    font-size: 40px;
  }
`

const HeroTagline = styled.p`
  font-size: 24px;
  font-weight: 300;
  color: rgba(255, 255, 255, 0.95);
  margin-bottom: 30px;

  @media (max-width: 800px) {
    font-size: 20px;
  }
`

const HeroDescription = styled.p`
  font-size: 18px;
  line-height: 1.8;
  color: rgba(255, 255, 255, 0.9);
  margin-bottom: 40px;
`

const CTAButton = styled.button`
  background: #ffffff;
  color: #6366f1;
  font-size: 18px;
  font-weight: 700;
  padding: 18px 50px;
  border: none;
  border-radius: 9999px;
  cursor: pointer;
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.3);
  transition: all 500ms ease-in-out;

  &:hover {
    transform: translate3d(0, -4px, 0);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.4);
    background: #c7d2fe;
    color: #4f46e5;
  }

  @media (max-width: 800px) {
    width: 100%;
    padding: 18px 40px;
  }
`

const Section = styled.section`
  padding: 100px 0;

  @media (max-width: 800px) {
    padding: 60px 0;
  }
`

const OverviewSection = styled(Section)`
  background: linear-gradient(180deg, #f7fafc 0%, #edf2f7 100%);
`

const SectionTitle = styled.h2`
  font-size: 36px;
  font-weight: 700;
  line-height: 1.3;
  color: #1a202c;
  text-align: center;
  margin-bottom: 20px;

  @media (max-width: 800px) {
    font-size: 28px;
  }
`

const SectionSubtitle = styled.p`
  font-size: 18px;
  line-height: 1.8;
  color: #4a5568;
  margin-bottom: 30px;
  text-align: center;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
`

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 40px;
  margin-top: 60px;

  @media (max-width: 800px) {
    grid-template-columns: 1fr;
    gap: 30px;
  }
`

const FeatureCard = styled.div`
  background: white;
  padding: 40px;
  border-radius: 16px;
  box-shadow: 0px 4px 20px rgba(99, 102, 241, 0.1);
  transition: all 500ms ease-in-out;
  text-align: center;

  &:hover {
    transform: translate3d(0, -10px, 0);
    box-shadow: 0px 12px 40px rgba(99, 102, 241, 0.2);
  }
`

const FeatureIcon = styled.div`
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, #6366f1 0%, #4f46e5 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 40px;
  margin: 0 auto 25px;
`

const FeatureTitle = styled.h3`
  font-size: 22px;
  font-weight: 700;
  color: #1a202c;
  margin-bottom: 15px;
`

const FeatureDescription = styled.p`
  font-size: 16px;
  line-height: 1.6;
  color: #4a5568;
`

const BenefitsSection = styled(Section)`
  background: linear-gradient(180deg, #ffffff 0%, #f7fafc 100%);
`

const BenefitsList = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 30px;
  margin-top: 60px;

  @media (max-width: 800px) {
    grid-template-columns: 1fr;
  }
`

const BenefitItem = styled.div`
  display: flex;
  gap: 20px;
  align-items: flex-start;
`

const BenefitIcon = styled.div`
  font-size: 32px;
  color: #6366f1;
  flex-shrink: 0;
`

const BenefitContent = styled.div``

const BenefitTitle = styled.h4`
  font-size: 20px;
  font-weight: 700;
  color: #1a202c;
  margin-bottom: 10px;
`

const BenefitText = styled.p`
  font-size: 16px;
  line-height: 1.6;
  color: #4a5568;
`

const HowItWorksSection = styled(Section)`
  background: linear-gradient(180deg, #edf2f7 0%, #e2e8f0 100%);
`

const StepsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 30px;
  margin-top: 60px;

  @media (max-width: 800px) {
    grid-template-columns: 1fr;
  }
`

const StepCard = styled.div`
  background: white;
  padding: 30px;
  border-radius: 12px;
  text-align: center;
  position: relative;
  box-shadow: 0px 4px 20px rgba(99, 102, 241, 0.1);
`

const StepNumber = styled.div`
  width: 50px;
  height: 50px;
  background: linear-gradient(135deg, #6366f1 0%, #4f46e5 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  font-weight: 700;
  color: white;
  margin: 0 auto 20px;
`

const StepTitle = styled.h4`
  font-size: 18px;
  font-weight: 700;
  color: #1a202c;
  margin-bottom: 10px;
`

const StepDescription = styled.p`
  font-size: 15px;
  line-height: 1.6;
  color: #4a5568;
`

const CTASection = styled(Section)`
  background: linear-gradient(135deg, #6366f1 0%, #4f46e5 100%);
  color: white;
  text-align: center;
`

const CTATitle = styled.h2`
  font-size: 36px;
  font-weight: 700;
  margin-bottom: 20px;
  color: white;

  @media (max-width: 800px) {
    font-size: 28px;
  }
`

const CTADescription = styled.p`
  font-size: 18px;
  margin-bottom: 40px;
  color: rgba(255, 255, 255, 0.95);
  max-width: 700px;
  margin-left: auto;
  margin-right: auto;
`

const CTAButtonWhite = styled(CTAButton)`
  background: white;
  color: #6366f1;

  &:hover {
    background: #c7d2fe;
    color: #4f46e5;
  }
`

const ZenoPolicy = () => {
  const navigate = useNavigate()

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  const features = [
    {
      icon: '👥',
      title: 'Lead Management',
      description: 'Capture, track, and nurture leads efficiently with a centralized system that helps convert prospects into clients with organized follow-ups and communication tracking.'
    },
    {
      icon: '📇',
      title: 'Client Management',
      description: 'Maintain comprehensive client profiles with complete history, policy details, contact information, and interaction records all in one place.'
    },
    {
      icon: '📝',
      title: 'Enrollment Processing',
      description: 'Streamline the enrollment process with easy-to-use forms, document collection, and automated workflows that reduce paperwork and errors.'
    },
    {
      icon: '🔄',
      title: 'Renewal Management',
      description: 'Never miss a renewal with automated reminders, tracking dashboards, and streamlined renewal workflows that boost retention rates.'
    },
    {
      icon: '📊',
      title: 'Reports & Analytics',
      description: 'Gain actionable insights with comprehensive dashboards, customizable reports, and performance metrics for better decision-making.'
    },
    {
      icon: '📱',
      title: 'Multi-Channel Communication',
      description: 'Engage with leads and clients through email, SMS, and in-app notifications with templates and automated communication workflows.'
    }
  ]

  const benefits = [
    {
      icon: '⏱️',
      title: 'Save Time on Admin Tasks',
      description: 'Automate routine tasks like follow-ups, reminders, and data entry to focus more on building client relationships and closing deals.'
    },
    {
      icon: '💎',
      title: 'Improve Client Retention',
      description: 'Keep clients engaged with timely renewal reminders, personalized communication, and exceptional service that builds loyalty.'
    },
    {
      icon: '💰',
      title: 'Increase Conversion Rates',
      description: 'Convert more leads into clients with organized tracking, timely follow-ups, and a streamlined enrollment process.'
    },
    {
      icon: '🎯',
      title: 'Stay Organized',
      description: 'Keep all your leads, clients, policies, and communications organized in one centralized platform accessible from anywhere.'
    },
    {
      icon: '📈',
      title: 'Scale Your Business',
      description: 'Handle growing client volumes effortlessly with a platform designed to scale with your business needs.'
    },
    {
      icon: '🌟',
      title: 'Better Client Experience',
      description: 'Deliver a professional, seamless experience from first contact through enrollment and ongoing policy management.'
    }
  ]

  const steps = [
    {
      number: '1',
      title: 'Capture Leads',
      description: 'Import leads from multiple sources, capture inquiries, and organize prospects in your pipeline.'
    },
    {
      number: '2',
      title: 'Nurture & Convert',
      description: 'Engage leads with timely follow-ups, track interactions, and move them through your sales funnel.'
    },
    {
      number: '3',
      title: 'Enroll Clients',
      description: 'Process enrollments efficiently with digital forms, document management, and streamlined workflows.'
    },
    {
      number: '4',
      title: 'Manage & Renew',
      description: 'Maintain client relationships, track policy details, and ensure timely renewals with automated reminders.'
    }
  ]

  return (
    <Container>
      <HeroSection>
        <MaxWidthContainer>
          <HeroContent>
            <ProductLogo>ZenoPolicy</ProductLogo>
            <HeroTagline>Simplify Lead & Client Management for Insurance Agents</HeroTagline>
            <HeroDescription>
              ZenoPolicy is a comprehensive CRM platform designed for insurance agents and agencies.
              Manage your leads, clients, enrollments, and renewals all in one place. Stay organized,
              close more deals, and grow your business with streamlined workflows.
            </HeroDescription>
            <CTAButton onClick={() => navigate('/contact')}>
              Request a Demo
            </CTAButton>
          </HeroContent>
        </MaxWidthContainer>
      </HeroSection>

      <OverviewSection>
        <MaxWidthContainer>
          <SectionTitle>Everything You Need to Manage Your Insurance Business</SectionTitle>
          <SectionSubtitle>
            ZenoPolicy brings all your essential tools together in one intuitive platform.
            From capturing new leads to managing client relationships and processing renewals,
            streamline your entire workflow and focus on what matters most - your clients.
          </SectionSubtitle>

          <FeaturesGrid>
            {features.map((feature, index) => (
              <FeatureCard key={index}>
                <FeatureIcon>{feature.icon}</FeatureIcon>
                <FeatureTitle>{feature.title}</FeatureTitle>
                <FeatureDescription>{feature.description}</FeatureDescription>
              </FeatureCard>
            ))}
          </FeaturesGrid>
        </MaxWidthContainer>
      </OverviewSection>

      <BenefitsSection>
        <MaxWidthContainer>
          <SectionTitle>Why Insurance Agents Choose ZenoPolicy</SectionTitle>
          <SectionSubtitle>
            Thousands of insurance professionals trust ZenoPolicy to streamline their operations,
            stay organized, and deliver exceptional client experiences that drive growth.
          </SectionSubtitle>

          <BenefitsList>
            {benefits.map((benefit, index) => (
              <BenefitItem key={index}>
                <BenefitIcon>{benefit.icon}</BenefitIcon>
                <BenefitContent>
                  <BenefitTitle>{benefit.title}</BenefitTitle>
                  <BenefitText>{benefit.description}</BenefitText>
                </BenefitContent>
              </BenefitItem>
            ))}
          </BenefitsList>
        </MaxWidthContainer>
      </BenefitsSection>

      <HowItWorksSection>
        <MaxWidthContainer>
          <SectionTitle>How ZenoPolicy Works</SectionTitle>
          <SectionSubtitle>
            From lead capture to client retention, ZenoPolicy streamlines every stage of your workflow.
          </SectionSubtitle>

          <StepsGrid>
            {steps.map((step, index) => (
              <StepCard key={index}>
                <StepNumber>{step.number}</StepNumber>
                <StepTitle>{step.title}</StepTitle>
                <StepDescription>{step.description}</StepDescription>
              </StepCard>
            ))}
          </StepsGrid>
        </MaxWidthContainer>
      </HowItWorksSection>

      <CTASection>
        <MaxWidthContainer>
          <CTATitle>Ready to Streamline Your Insurance Business?</CTATitle>
          <CTADescription>
            Join thousands of insurance agents who are growing their business with ZenoPolicy.
            Schedule a personalized demo and see how easy it is to manage leads, clients, and renewals.
          </CTADescription>
          <CTAButtonWhite onClick={() => navigate('/contact')}>
            Schedule Your Demo
          </CTAButtonWhite>
        </MaxWidthContainer>
      </CTASection>
    </Container>
  )
}

export default ZenoPolicy
